import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, collectionGroup } from 'firebase/firestore';
import { getDatabase, ref, get } from 'firebase/database';
import { db, auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import '../styles/global.css';
import LoadingSpinner from './LoadingSpinner';

const Vault = () => {
    const { user } = useAuth();
    const [quizzes, setQuizzes] = useState([]);
    const [filteredQuizzes, setFilteredQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    // UI Filters (Admin Only)
    const [filterHierarchy, setFilterHierarchy] = useState('');
    const [filterCorps, setFilterCorps] = useState('');
    const [filterSpecialty, setFilterSpecialty] = useState('');

    // Opciones dinámicas desde RTDB
    const [hierarchyOptions, setHierarchyOptions] = useState([]);
    const [corpsOptions, setCorpsOptions] = useState([]);
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [specialtiesByCorps, setSpecialtiesByCorps] = useState({});
    const [allSpecialties, setAllSpecialties] = useState([]);

    // Mapeos bidireccionales para normalización
    const [hierarchyMap, setHierarchyMap] = useState({ nameToCode: {}, codeToName: {} });
    const [corpsMap, setCorpsMap] = useState({ nameToCode: {}, codeToName: {} });
    const [specialtyMap, setSpecialtyMap] = useState({ nameToCode: {}, codeToName: {} });

    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);

    // Mapeo manual entre índices de RTDB y códigos de Firestore
    const RTDB_TO_FIRESTORE_HIERARCHY = {
        '0': 'MRO',           // MARINERO
        '1': 'CBO',           // CABO
        '2': '3RM',           // TERCER_MAESTRE
        '3': '2DM',           // SEGUNDO_MAESTRE
        '4': '1RM',           // PRIMER_MAESTRE
        '5': 'TTE.CORB.',     // TENIENTE_CORBETA
        '6': 'TTE.FRAG.',     // TENIENTE_FRAGATA
        '7': 'TTE.NAV.',      // TENIENTE_NAVIO
        '8': 'CAP.CORB.'      // CAPITAN_CORBETA
    };

    const RTDB_TO_FIRESTORE_CORPS = {
        '0': 'CG',    // Cuerpo General
        '1': 'IM',    // Infantería de Marina
        '2': 'AN',    // Aeronáutica Naval
        '3': 'MN'     // Mecánicos Navales
    };

    const RTDB_TO_FIRESTORE_SERVICES = {
        '0': 'SAIN',  // Administración e Intendencia Naval
        '1': 'SAN',   // Sanidad Naval
        '2': 'ING',   // Ingenieros de la Armada
        '3': 'JUS',   // Justicia Naval
        '4': 'DOC',   // Servicio de Docencia Naval
        '5': 'TS',    // Trabajo Social
        '6': 'MUS',   // Músicos Navales
        '7': 'SG'     // Servicios Generales
    };

    // Mapeo de nombres de especialidades a códigos de Firestore
    const SPECIALTY_NAME_TO_CODE = {
        'Oficinista': 'OFTA', 'Oficinistas': 'OFTA', 'Infanteria de Marina': 'IM', 'Infantería de Marina': 'INFANTERÍA DE MARINA', 'Lavandería': 'LAVD', 'Cocina': 'COCN',
        'Armamento': 'ARMM', 'Navegación': 'NAVG', 'Condestable': 'COND', 'Mecánico': 'MEC', 'Electricista': 'ELEC',
        'Electrónica': 'ELTC', 'Torpedos': 'TORP', 'Comunicaciones': 'COM', 'Radar': 'RAD', 'Sonar': 'SON',
        'Contramedidas': 'CMED', 'Piloto Aviador Naval': 'PAN', 'Observador Aéreo': 'OAN', 'Técnico Aeronáutico': 'TAN',
        'Fusilero': 'FUS', 'Artillero': 'ART', 'Médico': 'MED', 'Enfermero': 'ENF', 'Odontólogo': 'ODO', 'Farmacéutico': 'FAR',
        'Costurero': 'COSTR', 'Sastre': 'SSTR', 'Chofer': 'CHOFR', 'Lavandero': 'LAVDR', 'Jardinero': 'JRDNR',
        'Servicios Generales de alimentacion': 'SGA', 'impresor': 'IMPR', 'Camarero': 'CMRR', 'Cocinero': 'COCN',
        'Panadero': 'PNDR', 'Peluquero': 'PLQR', 'Armamento Aereo': 'ARMM_AER', 'Mecánico de Aviación': 'MEC_AVI',
        'Electrónico de Aviación': 'ELE_AVI', 'Operaciones Aereas': 'OPE_AER', 'Controlador de Vuelo': 'CTRL_VLO',
        'Contramaestres': 'CONTRAM', 'Condestables': 'CONDEST', 'Maquinas': 'MAQ', 'Trabajos Submarinos': 'TRAB_SUB',
        'Proteccion Portuaria': 'PROT_PORT', 'Informatico': 'INF', 'Radio Operador': 'RAD_OP', 'Mecánico Automotriz': 'MEC_AUTO',
        'Hidrografía': 'HIDRO', 'Informática/Computación': 'INF_COMP', 'carpintero ': 'CRPNT', 'Tornero': 'TRNR',
        'Soldador': 'SLDR', 'Electromecanico': 'E_MEC', 'Fontanero': 'FONT', 'Criminalistica': 'CRIM',
        'Licenciado en Derecho': 'DER', 'Mecánicos/Motoristas': 'MEC_MOT', 'Electricistas': 'ELEC',
        'Frigoríficos': 'FRIG', 'Control de Averías': 'CTRL_AV', 'Electrónicos': 'ELECTRON', 'Médicos Cirujanos': 'MED_CIR',
        'Cirujanos Dentistas': 'CIR_DENT', 'Enfermería': 'ENF', 'Psicología': 'PSIC', 'Nutrición': 'NUTR', 'Sanidad': 'SAN',
        'camillero ': 'CAMILL', 'Servicios Generales de Sanidad': 'SGS', 'Químico': 'QUIM', 'Dentista': 'DENT',
        'Radiologo': 'RADIO', 'Laboratorista': 'LAB', 'Licenciada en Pedagogia': 'PEDAG', 'Licenciada en Educacion Preescolar': 'EDUC_PRE',
        'Licenciada en Educacion Primaria': 'EDUC_PRIM', 'Alimentación': 'ALIM', 'Servicios Generales': 'SG',
        'Servicio de Trabajo Social Naval': 'TSN'
    };

    // Función auxiliar para crear mapeos bidireccionales
    const createBidirectionalMap = (metadataObject) => {
        const nameToCode = {};
        const codeToName = {};

        Object.entries(metadataObject).forEach(([code, value]) => {
            const name = typeof value === 'object' ? (value.nombre || code) : value;
            const upperCode = code.toUpperCase();

            // Mapear nombre -> código
            nameToCode[name] = upperCode;
            nameToCode[name.toUpperCase()] = upperCode;
            // Mapear código -> código (para búsqueda directa)
            nameToCode[code] = upperCode;
            nameToCode[upperCode] = upperCode;
            // Mapear código -> nombre
            codeToName[upperCode] = name;
        });

        return { nameToCode, codeToName };
    };

    // Función para normalizar códigos
    const normalizeCode = (value, mappingObject) => {
        if (!value) return null;

        // Intentar encontrar el código correspondiente
        const directMatch = mappingObject[value];
        if (directMatch) return directMatch;

        const upperValue = value.toUpperCase();
        const upperMatch = mappingObject[upperValue];
        if (upperMatch) return upperMatch;

        // Si no se encuentra, devolver el valor en mayúsculas
        return upperValue;
    };

    // Fetch metadata options from RTDB
    useEffect(() => {
        const fetchMetadata = async () => {
            const rtdb = getDatabase();

            // Fetch app_metadata for corps, specialties, and hierarchies
            try {
                const metadataRef = ref(rtdb, 'app_metadata');
                const metadataSnapshot = await get(metadataRef);

                console.log('[Vault] app_metadata exists:', metadataSnapshot.exists());

                if (metadataSnapshot.exists()) {
                    const data = metadataSnapshot.val();
                    console.log('[Vault] app_metadata data:', Object.keys(data));

                    // Extraer cuerpos y servicios desde app_metadata/cuerpos y app_metadata/servicios
                    const corpsList = [];
                    const corpsMetadataForMap = {};

                    if (data.cuerpos_y_servicios) {
                        Object.entries(data.cuerpos_y_servicios).forEach(([index, name]) => {
                            const idx = parseInt(index);
                            let firestoreCode;
                            let type;

                            if (idx >= 0 && idx <= 3) {
                                // Es un Cuerpo (0-3)
                                firestoreCode = RTDB_TO_FIRESTORE_CORPS[index] || `CG${index}`;
                                type = 'cuerpo';
                            } else {
                                // Es un Servicio (4-11, pero mapeado desde RTDB_TO_FIRESTORE_SERVICES que usaba 0-7)
                                // Restamos 4 para que coincida con el mapeo previo si es necesario o ajustamos el mapeo
                                const serviceIdx = (idx - 4).toString();
                                firestoreCode = RTDB_TO_FIRESTORE_SERVICES[serviceIdx] || `SRV${index}`;
                                type = 'servicio';
                            }

                            const uniqueId = `${type.toUpperCase()}_${firestoreCode}`;
                            corpsList.push({
                                code: uniqueId,
                                originalCode: firestoreCode,
                                rtdbIndex: index,
                                name: name,
                                type: type
                            });
                            corpsMetadataForMap[uniqueId] = name;
                            corpsMetadataForMap[firestoreCode] = name;
                        });
                    }
                    setCorpsOptions(corpsList);
                    console.log('[Vault] Corps and Services loaded:', corpsList.length);
                    console.log('[Vault] Corps list with Firestore codes:', corpsList);

                    // Crear mapeo bidireccional para cuerpos/servicios con IDs únicos
                    const corpsBiMap = createBidirectionalMap(corpsMetadataForMap);
                    setCorpsMap(corpsBiMap);
                    console.log('[Vault] Corps bidirectional map created:', Object.keys(corpsBiMap.nameToCode).length, 'entries');

                    // Extraer especialidades por cuerpo/servicio desde app_metadata/especialidades
                    const specialtiesMap = {};
                    if (data.especialidades) {
                        console.log('[Vault] especialidades found in app_metadata:', data.especialidades);
                        Object.entries(data.especialidades).forEach(([name, specs]) => {
                            if (Array.isArray(specs)) {
                                specialtiesMap[name] = specs;
                            }
                        });
                    setSpecialtiesByCorps(specialtiesMap);
                    }

                    // Crear lookups insensibles a mayúsculas para las constantes
                    const specialtyLookup = {};
                    Object.entries(SPECIALTY_NAME_TO_CODE).forEach(([name, code]) => {
                        specialtyLookup[name.toUpperCase()] = code;
                    });

                    // Extraer todas las especialidades únicas desde app_metadata/especialidades
                    const allSpecsSet = new Set();
                    if (data.especialidades) {
                        Object.values(data.especialidades).forEach(specs => {
                            if (Array.isArray(specs)) {
                                specs.forEach(spec => {
                                    if (spec) allSpecsSet.add(spec);
                                });
                            }
                        });
                    }
                    const allSpecs = Array.from(allSpecsSet).map(specName => {
                        const firestoreCode = specialtyLookup[specName.toUpperCase()] || specName;
                        console.log('[Vault] Mapping specialty:', specName, '→', firestoreCode);
                        return {
                            code: firestoreCode,
                            name: specName,
                            originalName: specName
                        };
                    });
                    setAllSpecialties(allSpecs);
                    setSpecialtyOptions(allSpecs); // Inicialmente todas
                    console.log('[Vault] All Specialties loaded from RTDB:', allSpecs.length);

                    // Crear mapeo bidireccional para especialidades usando códigos de Firestore
                    const specialtyMetadata = {};
                    Array.from(allSpecsSet).forEach(specName => {
                        const firestoreCode = specialtyLookup[specName.toUpperCase()] || specName;
                        specialtyMetadata[firestoreCode] = specName;
                    });
                    const specialtyBiMap = createBidirectionalMap(specialtyMetadata);
                    setSpecialtyMap(specialtyBiMap);
                    console.log('[Vault] Specialty bidirectional map created:', Object.keys(specialtyBiMap.nameToCode).length, 'entries');

                    // Extraer grados (jerarquías) desde app_metadata/grados
                    if (data.grados) {
                        console.log('[Vault] grados found in app_metadata:', data.grados);
                        const hierarchies = Object.entries(data.grados).map(([index, value]) => {
                            const firestoreCode = RTDB_TO_FIRESTORE_HIERARCHY[index] || `GRD${index}`;
                            return {
                                code: firestoreCode,
                                rtdbIndex: index,
                                name: value
                            };
                        });
                        setHierarchyOptions(hierarchies);

                        // Crear mapeo bidireccional para jerarquías usando códigos de Firestore
                        const hierarchyMetadata = {};
                        Object.entries(data.grados).forEach(([index, value]) => {
                            const firestoreCode = RTDB_TO_FIRESTORE_HIERARCHY[index] || `GRD${index}`;
                            hierarchyMetadata[firestoreCode] = value;
                        });
                        const hierarchyBiMap = createBidirectionalMap(hierarchyMetadata);
                        setHierarchyMap(hierarchyBiMap);
                        console.log('[Vault] Hierarchy bidirectional map created:', Object.keys(hierarchyBiMap.nameToCode).length, 'entries');
                    } else {
                        console.warn('[Vault] grados not found in app_metadata, using fallback');
                        setHierarchyOptions([
                            { code: 'MRO', name: 'Marinero' },
                            { code: 'CABO', name: 'Cabo' },
                            { code: '3ER.MTRE', name: 'Tercer Maestre' },
                            { code: '2DO.MTRE', name: 'Segundo Maestre' },
                            { code: '1ER.MTRE', name: 'Primer Maestre' },
                            { code: 'TTE.CORB.', name: 'Teniente de Corbeta' },
                            { code: 'TTE.FRAG.', name: 'Teniente de Fragata' },
                            { code: 'TTE.NAV.', name: 'Teniente de Navio' },
                            { code: 'CAP.CORB.', name: 'Capitán de Corbeta' }
                        ]);

                        // Crear mapeo bidireccional de fallback
                        const fallbackHierarchyMetadata = {
                            'MRO': 'Marinero',
                            'CBO': 'Cabo',
                            '3RM': 'Tercer Maestre',
                            '2DM': 'Segundo Maestre',
                            '1RM': 'Primer Maestre',
                            'SOF': 'Suboficial',
                            'TTE': 'Teniente',
                            'CAP': 'Capitán'
                        };
                        const fallbackHierarchyBiMap = createBidirectionalMap(fallbackHierarchyMetadata);
                        setHierarchyMap(fallbackHierarchyBiMap);
                    }
                }
            } catch (error) {
                console.error('[Vault] Error loading app_metadata:', error);
                // Set fallback hierarchies on error
                const fallbackHierarchyMetadata = {
                    'MRO': 'Marinero',
                    'CBO': 'Cabo',
                    '3RM': 'Tercer Maestre',
                    '2DM': 'Segundo Maestre',
                    '1RM': 'Primer Maestre',
                    'SOF': 'Suboficial',
                    'TTE': 'Teniente',
                    'CAP': 'Capitán'
                };
                const fallbackHierarchyBiMap = createBidirectionalMap(fallbackHierarchyMetadata);
                setHierarchyMap(fallbackHierarchyBiMap);

                setHierarchyOptions([
                    { code: 'MRO', name: 'Marinero' },
                    { code: 'CBO', name: 'Cabo' },
                    { code: '3RM', name: 'Tercer Maestre' },
                    { code: '2DM', name: 'Segundo Maestre' },
                    { code: '1RM', name: 'Primer Maestre' },
                    { code: 'SOF', name: 'Suboficial' },
                    { code: 'TTE', name: 'Teniente' },
                    { code: 'CAP', name: 'Capitán' }
                ]);
            }
        };

        fetchMetadata();
    }, []);

    // Actualizar especialidades basado en el filtro de cuerpo/servicio seleccionado
    useEffect(() => {
        if (!filterCorps) {
            setSpecialtyOptions(allSpecialties);
        } else {
            // Convertir código único a nombre para buscar especialidades
            const corpsName = corpsMap.codeToName[filterCorps];
            const corpsSpecialties = specialtiesByCorps[corpsName] || [];
            // Filtrar por nombre original ya que RTDB guarda nombres, no códigos
            const filteredSpecs = allSpecialties.filter(spec => corpsSpecialties.includes(spec.originalName || spec.name));
            setSpecialtyOptions(filteredSpecs);
            console.log('[Vault] Filtered specialties for corps:', corpsName, '(', filterCorps, '):', filteredSpecs.length);
            console.log('[Vault] Corps specialties from RTDB:', corpsSpecialties);
            console.log('[Vault] Filtered specialty objects:', filteredSpecs);
        }
    }, [filterCorps, allSpecialties, specialtiesByCorps, corpsMap]);

    // Funciones de traducción usando mapeos dinámicos
    const translateHierarchy = (h) => {
        if (!h) return 'MRO';
        return normalizeCode(h, hierarchyMap.nameToCode) || h.toUpperCase();
    };

    const translateCorps = (c) => {
        if (!c) return 'SAIN';
        return normalizeCode(c, corpsMap.nameToCode) || c.toUpperCase();
    };

    const translateSpecialty = (s) => {
        if (!s) return 'OFTA';
        return normalizeCode(s, specialtyMap.nameToCode) || s.toUpperCase();
    };

    // Cargar cuestionarios solo cuando el usuario y los mapas de metadatos estén listos
    useEffect(() => {
        const checkAndLoad = async () => {
            if (!user) return;
            
            // Esperar a que los mapas de metadatos tengan datos
            const mapsReady = Object.keys(hierarchyMap.nameToCode).length > 0 && 
                             Object.keys(corpsMap.nameToCode).length > 0 && 
                             Object.keys(specialtyMap.nameToCode).length > 0;
            
            if (!mapsReady) {
                console.log('[Vault] ⏳ Esperando a que se carguen los metadatos...');
                return;
            }

            try {
                setLoading(true);
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                let userData = {};
                if (userDoc.exists()) {
                    userData = userDoc.data();
                    setUserProfile(userData);
                }

                console.log('[Vault] 🚀 Metadatos listos, cargando cuestionarios...');
                await loadQuizzes(userData);
            } catch (error) {
                console.error("[Vault] Error loading initial data:", error);
            } finally {
                setLoading(false);
            }
        };

        checkAndLoad();
    }, [user, hierarchyMap, corpsMap, specialtyMap]);

    const loadQuizzes = async (profileData) => {
        setLoading(true);
        try {
            const hCodeUser = profileData.hierarchy || profileData.jerarquia || profileData.grado;
            const sCodeUser = profileData.specialty || profileData.especialidad;

            const hCodeNormal = translateHierarchy(hCodeUser);
            const sCodeNormal = translateSpecialty(sCodeUser);

            // Auto-inicializar filtros si están vacíos
            if (!filterHierarchy) setFilterHierarchy(hCodeNormal);
            if (!filterSpecialty) setFilterSpecialty(sCodeNormal);

            let results = [];
            const checkAdmin = profileData?.role === 'admin' || 
                ['admin@ascensos.com', 'fhernando67@gmail.com', 'soporte@ascensos.com', 'ascensosappvortex@gmail.com'].includes(user?.email);
            
            setIsAdmin(checkAdmin);

            if (checkAdmin) {
                // --- ADMIN: VER TODO ---
                const promises = [];

                // Buscar en shared_vault raíz (para algunos cuestionarios)
                promises.push(getDocs(query(
                    collection(db, 'shared_vault'),
                    where('isVault', '==', true)
                )));

                // Buscar en subcolecciones quizzes (para otros cuestionarios)
                promises.push(getDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('isVault', '==', true)
                )));

                // Buscar en Bóveda Global (Raíz) - Tipos Generales
                promises.push(getDocs(query(
                    collection(db, 'shared_vault'),
                    where('tipo', 'in', ['general', 'general_promoted'])
                )));

                const snapshots = await Promise.all(promises);

                // Deduplicar resultados por ID
                const uniqueMap = new Map();
                snapshots.forEach(snap => {
                    snap.docs.forEach(doc => {
                        const data = doc.data();

                        // Normalizar datos (igual que en vista de usuario)
                        const normalizedData = {
                            id: doc.id,
                            ...data,
                            name: data.titulo || data.name || 'Sin Título',
                            questionCount: data.preguntas?.length || data.questionCount || 0,
                            hierarchyCode: data.hierarchyCode?.toUpperCase() || '',
                            corpsCode: data.corpsCode?.toUpperCase() || '',
                            specialtyCode: data.specialtyCode?.toUpperCase() || '',
                            themeCode: data.themeCode || data.tipo || 'GENERAL'
                        };

                        uniqueMap.set(doc.id, normalizedData);
                    });
                });
                results = Array.from(uniqueMap.values());
            } else {
                // --- USUARIO: REGLAS DE SEGURIDAD ---
                const hCode = translateHierarchy(profileData.hierarchy || profileData.jerarquia || profileData.grado);
                const sCode = translateSpecialty(profileData.specialty || profileData.especialidad);
                // Cuerpo opcional si se requiere
                // const cCode = translateCorps(profileData.corps || profileData.cuerpo);

                console.log(`[Vault] 🔒 Aplicando filtros para: ${hCode} / ${sCode}`);

                const promises = [];

                // 1. Mis Archivos (Siempre visibles para el dueño) - en raíz y subcolecciones
                promises.push(getDocs(query(
                    collection(db, 'shared_vault'),
                    where('isVault', '==', true),
                    where('authorId', '==', user.uid)
                )));
                promises.push(getDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('isVault', '==', true),
                    where('authorId', '==', user.uid)
                )));

                // 2. Cuestionarios de la misma jerarquía y especialidad - en raíz y subcolecciones
                const hCodeReal = translateHierarchy(profileData.hierarchy || profileData.grado || profileData.jerarquia);
                const cCodeReal = translateCorps(profileData.corps || profileData.cuerpoServicio || profileData.cuerpo);
                const sCodeReal = translateSpecialty(profileData.specialty || profileData.especialidad);

                console.log(`[Vault] 🔒 Aplicando filtros estrictos: ${hCodeReal} | ${cCodeReal} | ${sCodeReal}`);

                // Función auxiliar para ejecutar consultas de forma segura
                const safeGetDocs = async (q, sourceName) => {
                    try {
                        return await getDocs(q);
                    } catch (err) {
                        console.warn(`[Vault] Consulta fallida en ${sourceName} (posible falta de índice):`, err.message);
                        return { docs: [] };
                    }
                };

                // Búsqueda técnica (Jerarquía + Especialidad)
                // Nota: Consultas con múltiples filtros en collectionGroup requieren índices compuestos.
                // Si fallan, el catch las ignorará y usaremos los filtros en memoria más adelante.
                promises.push(safeGetDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('isVault', '==', true),
                    where('hierarchyCode', '==', hCodeReal),
                    where('specialtyCode', '==', sCodeReal)
                ), 'Tech-H-S'));

                promises.push(safeGetDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('isVault', '==', true),
                    where('corpsCode', '==', cCodeReal),
                    where('specialtyCode', '==', sCodeReal)
                ), 'Tech-C-S'));

                // Búsqueda técnica amplia (Solo Especialidad) - Generalmente requiere menos índices
                promises.push(safeGetDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('specialtyCode', '==', sCodeReal)
                ), 'Tech-Spec-Only'));

                // 3. Bóveda Global Compartida
                console.log(`[Vault] 🌍 Buscando en Bóveda Global...`);
                
                promises.push(safeGetDocs(query(
                    collection(db, 'shared_vault'),
                    where('tipo', 'in', ['general', 'general_promoted'])
                ), 'Global-Root'));

                promises.push(safeGetDocs(query(
                    collectionGroup(db, 'quizzes'),
                    where('isVault', '==', true),
                    where('themeCode', 'in', ['GENERAL', 'LEGISLACION', 'MARINERIA'])
                ), 'Global-Sub'));

                // 3. Bóveda Global Compartida (Visible para todos)
                // Buscar documentos en la raíz de 'shared_vault' con tipo 'general' o 'general_promoted'
                console.log(`[Vault] 🌍 Buscando en Bóveda Global...`);
                promises.push(getDocs(query(
                    collection(db, 'shared_vault'),
                    where('tipo', 'in', ['general', 'general_promoted'])
                )));

                const snapshots = await Promise.all(promises);

                // Deduplicar y filtrar resultados en memoria (Más robusto ante falta de índices)
                const uniqueMap = new Map();
                snapshots.forEach(snap => {
                    snap.docs.forEach(doc => {
                        const data = doc.data();
                        
                        // Filtrado de seguridad en memoria si la consulta fue muy amplia
                        const docH = data.hierarchyCode?.toUpperCase() || '';
                        const docS = data.specialtyCode?.toUpperCase() || '';
                        const docC = data.corpsCode?.toUpperCase() || '';
                        const docT = data.themeCode?.toUpperCase() || data.tipo?.toUpperCase() || '';
                        const isVaultDoc = data.isVault === true || ['general', 'general_promoted'].includes(data.tipo);

                        // Regla: Debe ser Vault Y (Coincidir con Especialidad y Jerarquía O ser Global)
                        // Relajamos la verificación del cuerpo (docC) si no está presente, ya que la especialidad (docS)
                        // es lo suficientemente estricta para separar médicos de infantes de marina, etc.
                        const matchesTech = docH === hCodeReal && docS === sCodeReal;
                        const isGlobal = ['GENERAL', 'LEGISLACION', 'MARINERIA'].includes(docT) || 
                                       ['general', 'general_promoted'].includes(data.tipo);

                        if (isVaultDoc && (matchesTech || isGlobal || data.authorId === user.uid)) {
                            const normalizedData = {
                                id: doc.id,
                                ...data,
                                name: data.titulo || data.name || 'Sin Título',
                                questionCount: data.preguntas?.length || data.questionCount || 0,
                                hierarchyCode: docH,
                                corpsCode: docC,
                                specialtyCode: docS,
                                themeCode: docT || 'GENERAL'
                            };
                            uniqueMap.set(doc.id, normalizedData);
                        }
                    });
                });
                results = Array.from(uniqueMap.values());
            }

            setQuizzes(results);
            setFilteredQuizzes(results); // Guardamos copia para filtrado local (busqueda)
        } catch (error) {
            console.error("Error loading quizzes:", error);
            if (error.code === 'failed-precondition') {
                alert("Falta un índice en Firestore para esta consulta combinada. Revisa la consola.");
            }
        }
        setLoading(false);
    };

    const importQuiz = async (sharedQuiz) => {
        console.log(`[Vault] 📥 Iniciando importación de: ${sharedQuiz.name}`);
        if (!await window.confirmAsync(`¿Deseas importar "${sharedQuiz.name}" a tus cuestionarios personales?`)) return;

        try {
            setIsImporting(true);
            setImportProgress(10); // Inicio

            // 1. DETERMINAR AREA ID (Resiliencia para cuestionarios antiguos o mal formados)
            let areaId = sharedQuiz.areaId || sharedQuiz.area_id;

            // Si el areaId es el string 'specialty' (guardado por versiones previas de VortexDocs), convertir a 3
            if (areaId === 'specialty' || areaId === 'especialidad') {
                areaId = 3;
            }

            if (!areaId || areaId === 0 || areaId === '0') {
                // Si no tiene un areaId numérico válido, intentamos deducirlo:
                const sCode = sharedQuiz.specialtyCode;
                const nameUpper = (sharedQuiz.name || '').toUpperCase();
                const themeUpper = (sharedQuiz.themeCode || '').toUpperCase();
                const idUpper = (sharedQuiz.standardizedId || '').toUpperCase();
                
                if (
                    (sCode && !['GENERAL', 'NINGUNA', 'N/A', 'TODOS'].includes(sCode.toUpperCase())) ||
                    (sharedQuiz.hierarchyCode && !['GENERAL', 'NINGUNA', 'N/A', 'TODOS'].includes(sharedQuiz.hierarchyCode.toUpperCase())) ||
                    (sharedQuiz.corpsCode && !['GENERAL', 'NINGUNA', 'N/A', 'TODOS'].includes(sharedQuiz.corpsCode.toUpperCase())) ||
                    nameUpper.includes('DAM') || 
                    themeUpper.includes('DAM') ||
                    idUpper.includes('DAM') ||
                    nameUpper.includes('ADIESTRAMIENTO') ||
                    themeUpper.includes('ADIESTRAMIENTO') ||
                    idUpper.includes('ADIESTRAMIENTO')
                ) {
                    areaId = 3; // Conocimientos Específicos
                } else if (
                    nameUpper.includes('LEGISLACION') || 
                    themeUpper.includes('LEGISLACION') || 
                    idUpper.includes('LEGISLACION') ||
                    nameUpper.includes('LEY') || 
                    themeUpper.includes('LEY') ||
                    idUpper.includes('LEY')
                ) {
                    areaId = 2; // Legislación Naval
                } else {
                    areaId = 3; // Fallback a Conocimientos Específicos
                }
            }

            const newQuizData = {
                ...sharedQuiz,
                userId: user.uid,
                userName: user.displayName || 'Usuario',
                areaId: parseInt(areaId) || 1,
                createdAt: serverTimestamp(),
                importedFrom: sharedQuiz.id,
                completionCount: 0,
                averageScore: 0
            };
            // Limpiamos campos que no queremos copiar tal cual
            delete newQuizData.id;
            delete newQuizData.timestamp;
            delete newQuizData.isVault; // Ya no es un ítem de bóveda en mi lista personal

            const userQuizzesRef = collection(db, 'user_quizzes', user.uid, 'quizzes');
            const quizRef = await addDoc(userQuizzesRef, newQuizData);

            setImportProgress(30); // Cuestionario creado

            // 2. Obtener preguntas — la ruta depende de dónde fue publicado el cuestionario:
            //    A) Con authorId  → shared_vault/{authorId}/quizzes/{quizId}/questions
            //    B) Sin authorId  → shared_vault/{quizId}/questions  (cuestionarios raíz/globales)
            //    C) Sin authorId y sin subcolección → usar campo 'preguntas' embebido
            let questionsSnapshot = { docs: [] };

            if (sharedQuiz.authorId) {
                // Path A: Cuestionario publicado por un usuario específico
                console.log(`[Vault] 📂 Path A: shared_vault/${sharedQuiz.authorId}/quizzes/${sharedQuiz.id}/questions`);
                try {
                    const pathA = collection(db, 'shared_vault', sharedQuiz.authorId, 'quizzes', sharedQuiz.id, 'questions');
                    questionsSnapshot = await getDocs(pathA);
                    console.log(`[Vault] ✅ Path A encontró ${questionsSnapshot.docs.length} preguntas`);
                } catch (pathAError) {
                    console.warn('[Vault] Path A falló:', pathAError.message);
                }
            }

            // Si no se encontraron preguntas en Path A (o no había authorId), intentar Path B
            if (questionsSnapshot.docs.length === 0) {
                console.log(`[Vault] 📂 Path B: shared_vault/${sharedQuiz.id}/questions`);
                try {
                    const pathB = collection(db, 'shared_vault', sharedQuiz.id, 'questions');
                    questionsSnapshot = await getDocs(pathB);
                    console.log(`[Vault] ✅ Path B encontró ${questionsSnapshot.docs.length} preguntas`);
                } catch (pathBError) {
                    console.warn('[Vault] Path B falló:', pathBError.message);
                }
            }

            const normalizeForSave = (q) => {
                if (!q) return q;
                const preguntaText = q.pregunta || q.question || '';
                const explicacionText = q.explicacion || q.explanation || '';
                let respuestasMap = {};

                if (q.respuestas && typeof q.respuestas === 'object' && !Array.isArray(q.respuestas)) {
                    respuestasMap = q.respuestas;
                } else if (q.respuestas && Array.isArray(q.respuestas)) {
                    q.respuestas.forEach((resp, idx) => {
                        const respId = resp.id !== undefined ? resp.id : idx;
                        respuestasMap[respId] = {
                            id: respId,
                            respuesta: resp.respuesta || resp.text || '',
                            correcta: resp.correcta === true || resp.correcta === 1 || String(resp.correcta) === "1" || String(resp.correcta).toLowerCase() === "true"
                        };
                    });
                } else {
                    const opcionesArray = q.opciones || q.options || [];
                    if (Array.isArray(opcionesArray) && opcionesArray.length > 0) {
                        const correctIdx = q.correctIndex !== undefined ? q.correctIndex : (q.correctAnswer !== undefined ? q.correctAnswer : 0);
                        opcionesArray.forEach((opt, idx) => {
                            respuestasMap[idx] = {
                                id: idx,
                                respuesta: opt,
                                correcta: idx === parseInt(correctIdx)
                            };
                        });
                    }
                }

                return {
                    ...q,
                    pregunta: preguntaText,
                    explicacion: explicacionText,
                    respuestas: respuestasMap
                };
            };

            const totalQuestions = questionsSnapshot.docs.length;
            const quizQuestionsRef = collection(db, 'user_quizzes', user.uid, 'quizzes', quizRef.id, 'questions');

            if (totalQuestions > 0) {
                // Copiar preguntas desde la subcolección encontrada
                for (let i = 0; i < totalQuestions; i++) {
                    const qDoc = questionsSnapshot.docs[i];
                    await addDoc(quizQuestionsRef, {
                        ...normalizeForSave(qDoc.data()),
                        addedAt: serverTimestamp()
                    });

                    // Calcular progreso: de 30% a 95%
                    const progress = 30 + Math.floor(((i + 1) / totalQuestions) * 65);
                    setImportProgress(progress);
                }
            } else if (sharedQuiz.preguntas && Array.isArray(sharedQuiz.preguntas) && sharedQuiz.preguntas.length > 0) {
                // Path C: Preguntas embebidas en el campo 'preguntas' del documento raíz
                console.log(`[Vault] 📂 Path C: usando campo 'preguntas' embebido (${sharedQuiz.preguntas.length} preguntas)`);
                for (let i = 0; i < sharedQuiz.preguntas.length; i++) {
                    await addDoc(quizQuestionsRef, {
                        ...normalizeForSave(sharedQuiz.preguntas[i]),
                        addedAt: serverTimestamp()
                    });
                    const progress = 30 + Math.floor(((i + 1) / sharedQuiz.preguntas.length) * 65);
                    setImportProgress(progress);
                }
            } else {
                // Sin preguntas en ningún path — importar el cuestionario vacío con advertencia
                console.warn(`[Vault] ⚠️ No se encontraron preguntas para: ${sharedQuiz.name}. El cuestionario se importó vacío.`);
            }


            setImportProgress(100);
            console.log(`[Vault] ✨ Importación completada. Nuevo Quiz ID: ${quizRef.id}`);

            setTimeout(() => {
                setIsImporting(false);
                setImportProgress(0);
                alert('¡Cuestionario importado con éxito! Puedes encontrarlo en la sección de "Cuestionarios" dentro de su área correspondiente.');
            }, 500);

        } catch (error) {
            console.error("Error importing quiz:", error);
            alert("Error al importar el cuestionario.");
            setIsImporting(false);
            setImportProgress(0);
        }
    };

    if (loading) return <div className="flex-center" style={{ height: '60vh' }}><LoadingSpinner label="Cargando datos..." height="300px" /></div>;

    const visibleQuizzes = quizzes.filter(q => {
        const matchesSearch = q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            q.themeCode?.toLowerCase().includes(searchTerm.toLowerCase());

        // Admin Filters con normalización de códigos
        const normalizedQuizHierarchy = normalizeCode(q.hierarchyCode, hierarchyMap.nameToCode);
        const normalizedQuizCorps = normalizeCode(q.corpsCode, corpsMap.nameToCode);
        const normalizedQuizSpecialty = normalizeCode(q.specialtyCode, specialtyMap.nameToCode);

        const matchesH = !filterHierarchy || normalizedQuizHierarchy === filterHierarchy;

        // Para corps, necesitamos comparar con el código original (sin prefijo CUERPO_/SERVICIO_)
        let matchesC = !filterCorps;
        if (filterCorps) {
            // Extraer el código original del filtro seleccionado
            const filterOriginalCode = filterCorps.replace(/^(CUERPO_|SERVICIO_)/, '');
            matchesC = normalizedQuizCorps === filterOriginalCode ||
                normalizedQuizCorps === filterCorps;
        }

        const matchesS = !filterSpecialty || filterSpecialty === 'GLOBAL' || normalizedQuizSpecialty === filterSpecialty ||
            normalizedQuizSpecialty === 'GLOBAL' || normalizedQuizSpecialty === 'TODOS';

        const isGeneral = q.tipo?.toLowerCase() === 'general' || q.themeCode === 'GENERAL';

        return matchesSearch && (isGeneral || (matchesH && matchesC && matchesS));
    });

    return (
        <div className="vault-container">
            <header className="vault-header">
                <h1>🛡️ Bóveda Técnica</h1>
                <p style={{ color: 'var(--text-secondary)', marginTop: '10px' }}>
                    Repositorio compartido de conocimiento especializado.
                </p>
                {isAdmin ? (
                    <div className="vault-badge admin">
                        ADMINISTRADOR: VISIBILIDAD TOTAL
                    </div>
                ) : userProfile && (
                    <div className="vault-badge user">
                        TU FILTRO: {translateHierarchy(userProfile.hierarchy || userProfile.grado || userProfile.jerarquia)} | {translateSpecialty(userProfile.specialty || userProfile.especialidad)}
                    </div>
                )}
            </header>

            <div style={{ paddingBottom: '100px' }}>
                <div className="admin-filters">
                    <select value={filterHierarchy} onChange={e => setFilterHierarchy(e.target.value)}>
                        <option value="">Todas las Jerarquías</option>
                        <option value="GLOBAL">Global / Todas</option>
                        {hierarchyOptions.map((h, index) => (
                            <option key={`${h.code}-${index}`} value={h.code}>{h.name}</option>
                        ))}
                    </select>
                    <select value={filterCorps} onChange={e => setFilterCorps(e.target.value)}>
                        <option value="">Todos los Cuerpos y Servicios</option>
                        <option value="GLOBAL">Global / Todos</option>
                        {corpsOptions.map((c, index) => (
                            <option key={`${c.code}-${index}`} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                    <select value={filterSpecialty} onChange={e => setFilterSpecialty(e.target.value)}>
                        <option value="">Todas las Especialidades</option>
                        <option value="GLOBAL">Global / Todas</option>
                        {specialtyOptions.map((s, index) => (
                            <option key={`${s.code}-${index}`} value={s.code}>{s.name}</option>
                        ))}
                    </select>
                </div>

                <div className="search-bar" style={{ marginBottom: '30px' }}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar por nombre o tema (ej: Excel, Leyes...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="vault-search-input"
                    />
                </div>

                <div className="quiz-grid">
                    {visibleQuizzes.map(quiz => (
                        <div
                            key={quiz.id}
                            className="vault-quiz-card"
                            onClick={() => importQuiz(quiz)}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>📚</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h4>{quiz.name}</h4>
                                    <p className="theme-text">{quiz.themeCode || 'General'}</p>
                                    <div className="stats-row">
                                        <span>📝 {quiz.questionCount}</span>
                                        <span>👤 {quiz.authorName?.split(' ')[0] || 'Anónimo'}</span>
                                    </div>
                                    {isAdmin && (
                                        <div style={{
                                            fontSize: '0.65rem',
                                            color: '#888',
                                            fontFamily: 'monospace'
                                        }}>
                                            {quiz.hierarchyCode}-{quiz.corpsCode}-{quiz.specialtyCode}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                className="btn-import"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    importQuiz(quiz);
                                }}
                            >
                                📥 Importar
                            </button>
                        </div>
                    ))}
                </div>

                {quizzes.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '50px', color: 'var(--text-secondary)' }}>
                        <p>No hay cuestionarios compartidos todavía para tu especialidad.</p>
                        <p>¡Sé el primero en publicar uno desde el Generador!</p>
                    </div>
                )}
            </div>

            {/* Overlay de Importación */}
            {isImporting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(10, 14, 23, 0.9)',
                    backdropFilter: 'blur(10px)',
                    zIndex: 2000,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                }}>
                    <div className="import-card" style={{
                        background: 'var(--glass-bg)',
                        padding: '40px',
                        borderRadius: '24px',
                        border: '1px solid var(--glass-border)',
                        textAlign: 'center',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: 'var(--neon-glow)'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📥</div>
                        <h2 style={{ marginBottom: '10px' }}>Importando Cuestionario</h2>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '30px', fontSize: '0.9rem' }}>
                            Estamos preparando todo en tu biblioteca personal. Por favor espera...
                        </p>

                        <div className="progress-container" style={{
                            width: '100%',
                            height: '10px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            marginBottom: '15px'
                        }}>
                            <div className="progress-bar" style={{
                                width: `${importProgress}%`,
                                height: '100%',
                                background: 'var(--accent)',
                                boxShadow: '0 0 15px var(--accent)',
                                transition: 'width 0.3s ease-out'
                            }}></div>
                        </div>
                        <div style={{ fontWeight: 'bold', color: 'var(--accent)', fontSize: '1.2rem' }}>
                            {importProgress}%
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vault;
